<template>
  <!--
    BrowseColumns — the WIDE / desktop "Miller columns" browse for the
    insurance catalog. Finder-style: each column is the set of children of the
    node selected in the previous column. Depth is expressed by COLUMNS, not by
    indentation or outline numbers — so a deeply nested node is reached by
    walking rightward, never by reading "1.2.3".

    Purely presentational: NO Vuex, NO API. The parent owns the data shape and
    the selection. We are handed:
      - `roots`         the top-level nodes (column 0)
      - `childrenOf`    a Map UpperID → node[]  (key 0 holds the roots too, but
                        we use the `roots` prop for column 0 for clarity)
      - `selectionPath` the chosen node id at each depth, root → deep
      - `selectedId`    the id of the focused node (the leaf of the path)

    We DERIVE the visible columns from `selectionPath` + `childrenOf` and emit
    `select(node)` on any row click. The parent recomputes `selectionPath`; we
    just reflect the new props. Rows reuse the shared InsNodeRow.
  -->
  <div
    ref="strip"
    class="browse-cols"
    role="tree"
    :aria-label="$t('lmg.insure_browse_columns_aria')"
  >
    <div
      v-for="(col, ci) in columns"
      :key="'col-' + ci"
      class="browse-cols__col"
    >
      <!-- column header: the parent's Name (stripped for display when the
           toggle is on), or the roots label for column 0 (never stripped).
           `headerTitle` keeps the full real name for the hover tooltip. -->
      <div class="browse-cols__head" :title="col.headerTitle">
        <span class="browse-cols__head-text">{{
          col.headerStrip ? displayName(col.headerTitle, stripPrefix) : col.headerTitle
        }}</span>
      </div>

      <!-- the scrollable list of rows for this column -->
      <div class="browse-cols__list" role="group">
        <ins-node-row
          v-for="node in col.nodes"
          :key="node.ID"
          :node="node"
          :has-children="hasChildren(node)"
          :selected="node.ID === col.activeId"
          :expanded="node.ID === col.activeId && hasChildren(node)"
          :strip-prefix="stripPrefix"
          :hide-type-chip="hideTypeChip"
          @select="onSelect"
        />

        <!-- empty column (e.g. a node whose children haven't loaded / a level
             with no items): keep the column visible so the strip width is
             stable, but say so. -->
        <div v-if="col.nodes.length === 0" class="browse-cols__empty">
          {{ $t("lmg.insure_browse_empty") }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import InsNodeRow from "./InsNodeRow.vue";
import { displayName } from "./insName";

const ROOT_KEY = 0;

export default {
  name: "browse-columns",
  components: { InsNodeRow },
  props: {
    // top-level nodes — column 0.
    roots: { type: Array, default: () => [] },
    // Map<UpperID(Number), node[]>; key 0 = roots. Used to derive every column
    // beyond the first and to know whether a node "has children".
    childrenOf: { type: Map, default: () => new Map() },
    // selected node id at each depth, root → deep. Drives which column unfolds
    // next and which row in each column is the "active" (path-continuing) one.
    selectionPath: { type: Array, default: () => [] },
    // the focused node id (leaf of the path). Kept for parity with the contract
    // / breadcrumb sibling; selection highlight is driven by selectionPath so
    // EVERY column on the path shows its active row, not just the deepest.
    selectedId: { type: Number, default: null },
    // DISPLAY-ONLY: hide leading outline numbers from shown names.
    stripPrefix: { type: Boolean, default: false },
    // DISPLAY-ONLY: hide the per-row dataType chip (passed through to rows).
    hideTypeChip: { type: Boolean, default: false },
  },
  computed: {
    /**
     * Build the array of columns to render.
     *
     *   column 0 .nodes        = roots
     *   column 0 .activeId     = selectionPath[0]
     *
     *   column i+1 .nodes      = childrenOf.get(selectionPath[i]) || []
     *   column i+1 .activeId   = selectionPath[i+1]
     *
     * We stop adding columns as soon as the node selected at a depth has no
     * children to show (childrenOf has no / empty entry) — that node is a leaf
     * and there is nothing further to the right.
     *
     * Each column also carries a `headerTitle`: column 0 = the roots label;
     * deeper columns = the Name of the parent node that produced them.
     */
    columns() {
      const cols = [];

      // column 0 — the roots.
      cols.push({
        nodes: this.roots,
        activeId: this.idAtDepth(0),
        headerTitle: this.$t("lmg.insure_browse_roots"),
        headerStrip: false, // i18n roots label — never strip.
      });

      // walk the selection path; each selected node that HAS children opens the
      // next column. `parentNode` lets us label that column with the parent's
      // Name.
      let depth = 0;
      let parentNode = this.nodeById(this.roots, this.idAtDepth(0));

      while (parentNode) {
        const kids = this.childrenOf.get(parentNode.ID) || [];
        if (kids.length === 0) break; // leaf on the path → stop unfolding.

        depth += 1;
        cols.push({
          nodes: kids,
          activeId: this.idAtDepth(depth),
          headerTitle: parentNode.Name, // FULL real name (used as hover title).
          headerStrip: true, // a real node name — strip for display when toggled.
        });

        // advance: the active node in THIS new column becomes next parent.
        parentNode = this.nodeById(kids, this.idAtDepth(depth));
      }

      return cols;
    },
  },
  watch: {
    // when the path deepens (or shifts), make sure the freshly-opened, deepest
    // columns are scrolled into view so node names are never occluded off the
    // right edge. Watch the serialized path so any change re-triggers.
    selectionPath: {
      handler() {
        this.$nextTick(this.scrollToEnd);
      },
      deep: true,
    },
  },
  methods: {
    displayName,
    // selected node id at a given depth (null if the path is shorter).
    idAtDepth(depth) {
      const id = this.selectionPath[depth];
      return id == null ? null : id;
    },
    // does this node have at least one child to drill into?
    hasChildren(node) {
      const kids = this.childrenOf.get(node.ID);
      return !!kids && kids.length > 0;
    },
    // find a node by id within a given list (the column's nodes), or null.
    nodeById(list, id) {
      if (id == null || !list) return null;
      for (let i = 0; i < list.length; i += 1) {
        if (list[i].ID === id) return list[i];
      }
      return null;
    },
    onSelect(node) {
      // pure passthrough — parent recomputes selectionPath & feeds it back.
      this.$emit("select", node);
    },
    // horizontally scroll the strip so the rightmost columns are visible.
    scrollToEnd() {
      const strip = this.$refs.strip;
      if (!strip) return;
      strip.scrollLeft = strip.scrollWidth;
    },
  },
};
</script>

<style scoped>
/* The strip: a horizontal row of fixed-width columns. Overflow scrolls
   horizontally (depth-6 catalogs run off the right edge by design). Momentum
   scroll on iPad. */
.browse-cols {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  height: 100%;
  min-height: 0;
  overflow-x: auto;
  overflow-y: hidden;
  background: var(--c-surface, #fff);
  -webkit-overflow-scrolling: touch; /* iPad momentum */
  scroll-behavior: smooth;
}

.browse-cols__col {
  flex: 0 0 220px; /* fixed column width — names never get squeezed */
  width: 220px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-right: 1px solid var(--c-border, #e4e9f1); /* divider between cols */
}
.browse-cols__col:last-child {
  border-right: 0;
}

/* column header — the parent's Name (or the roots label) */
.browse-cols__head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  min-height: 34px;
  padding: 6px 12px;
  box-sizing: border-box;
  background: var(--c-blue-soft, #ebf2fa);
  border-bottom: 1px solid var(--c-border, #e4e9f1);
}
.browse-cols__head-text {
  font-size: 12px;
  font-weight: 600;
  color: var(--c-text-soft, #5e6c80);
  white-space: nowrap; /* Thai never wraps */
  overflow: hidden;
  text-overflow: ellipsis;
}

/* the per-column scroll region — independent vertical scroll, momentum on iPad */
.browse-cols__list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

.browse-cols__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 8px 12px;
  color: var(--c-text-mute, #8a96a8);
  font-size: 12px;
  text-align: center;
}
</style>
